import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllPostsData, deletePostsData } from "../services/Posts";
import { ListView } from "../CommonComponents/ListView/ListView";
import { FilterPageLayout } from "../CommonComponents/FilterPageLayout/FilterPageLayout";
import { useFilterPage } from "../hooks/useFilterPage";
import { explore } from "../constants/constants";
import { getImageURL } from "../util/getImage";
import { Image, Box, Text, Tooltip, Button, useToast } from "@chakra-ui/react";
import { handleExport as handleExportUtil } from "../util/helper";
import { showErrorToast, showSuccessToast } from "../util/errorHandler";

const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const placeholderImg = `${IMAGE_BASE_PATH}/ui/DummyImage/small.png`;

const combineTags = (item) => {
  const allTags = [
    ...(Array.isArray(item?.masterlist_tag_alias)
      ? item.masterlist_tag_alias
      : []),
    ...(Array.isArray(item?.provisional_masterlists_tag_alias)
      ? item.provisional_masterlists_tag_alias
      : []),
    ...(Array.isArray(item?.keyword_masterlist_tag_alias)
      ? item.keyword_masterlist_tag_alias
      : []),
  ];
  return [...new Set(allTags)]
    .filter((tag) => tag && tag.trim() !== "")
    .join(", ");
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "N/A";
  }
};

const citationFactor = (data) =>
  data?.map((item) => ({
    _id: item?._id,
    title: item?.title,
    alias: item?.alias,
    isVisible: item?.isVisible,
    direct_url_of_cover_image: item?.image,
    research_category_tag: Array.isArray(item?.research_category_tag)
      ? item.research_category_tag.join(", ")
      : item?.research_category_tag || "",
    combined_tags: combineTags(item),
    post_number: item?.post_number || 0,
    show_at: item?.show_at || "",
    explore_search_hint_order: item?.explore_search_hint_order ?? null,
  }));

const columns = [
  { label: "Post Number", accessor: "post_number" },
  { label: "Title", accessor: "title" },
  {
    label: "Publish Status",
    accessor: "isVisible",
    render: (value) => (value ? "Yes" : "No"),
  },
  {
    label: "Search Hint Order",
    accessor: "explore_search_hint_order",
    render: (value) =>
      value != null ? value : <Text color="gray.500">-</Text>,
  },
  {
    label: "Image",
    accessor: "direct_url_of_cover_image",
    render: (value) => (
      <Image
        src={getImageURL(value, "200", "100")}
        width="200px"
        height="100px"
        objectFit="contain"
        fallbackSrc={placeholderImg}
        alt="Cover image"
      />
    ),
  },
  {
    label: "Show At",
    accessor: "show_at",
    render: (value) => formatDate(value),
  },
  {
    label: "Research Category",
    accessor: "research_category_tag",
    render: (value) =>
      value ? (
        <Text noOfLines={2}>{value}</Text>
      ) : (
        <Text color="gray.500">-</Text>
      ),
  },
  {
    label: "Tags",
    accessor: "combined_tags",
    render: (value) =>
      value ? (
        <Tooltip label={value} placement="top" hasArrow>
          <Text noOfLines={2}>{value}</Text>
        </Tooltip>
      ) : (
        <Text color="gray.500">-</Text>
      ),
  },
];

const PostsTable = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const toast = useToast();
  const navigate = useNavigate();

  const handleEdit = (record) => {
    navigate(`/posts/${record?.alias || record?._id}`);
  };

  const filterState = useFilterPage({
    limit: 50,
    fetchFn: getAllPostsData,
    normalizeResult: (raw) => ({
      records: citationFactor(raw?.data?.data || []),
      total: raw?.data?.total,
      facetsHeaders: raw?.data?.facetsHeaders,
      facets: raw?.data?.facets,
      pageNumber: raw?.data?.page_number,
    }),
  });

  const {
    records,
    currentPage,
    totalPages,
    handlePageChange,
    loading,
    setLoading,
    searchText,
    fetchData,
    filterOptions,
    setFilterOptions,
    setApply,
  } = filterState;

  const sortOrder = filterOptions.sortBy_v === "title_desc" ? "desc" : "asc";
  const sortRecords = () => {
    const next = sortOrder === "asc" ? "title_desc" : "title";
    setFilterOptions((prev) => ({ ...prev, sortBy_v: next }));
    setApply(true);
  };

  const getRowKey = (record) => record?._id;

  const handleRowSelect = (key, checked) =>
    setSelectedIds((prev) =>
      checked
        ? prev.includes(key)
          ? prev
          : [...prev, key]
        : prev.filter((k) => k !== key),
    );

  const handleSelectAll = (checked) =>
    setSelectedIds(checked ? records.map((r) => r?._id).filter(Boolean) : []);

  const performDelete = async (idsToDelete) => {
    if (!idsToDelete?.length) return;
    const confirmed = window.confirm(
      `Delete ${idsToDelete.length} post(s)? This cannot be undone.`,
    );
    if (!confirmed) return;
    setLoading(true);
    try {
      await deletePostsData(idsToDelete);
      showSuccessToast(toast, `Deleted ${idsToDelete.length} post(s)`);
      setSelectedIds([]);
      await fetchData(currentPage, searchText, filterOptions);
    } catch (error) {
      console.error("Error deleting posts:", error);
      showErrorToast(toast, error?.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const handleRowDelete = (key) => performDelete([key]);
  const handleDeleteSelected = () => performDelete(selectedIds);
  const handleExport = () =>
    handleExportUtil("posts", toast, { search: searchText });

  const bulkDeleteBar = selectedIds.length > 0 && (
    <Box px={4} py={2}>
      <Button size="sm" colorScheme="red" onClick={handleDeleteSelected}>
        Delete Selected ({selectedIds.length})
      </Button>
    </Box>
  );

  return (
    <FilterPageLayout
      hook={filterState}
      toolbar={{
        title: "Posts",
        type: "posts",
        addOption: true,
        createNewRecord: () => navigate("/posts/create"),
        exportHandler: handleExport,
        sortRecords,
        sortOrder,
        pagination: false,
      }}
      filterBox={{ explore }}
      middleContent={bulkDeleteBar}
    >
      <ListView
        loading={loading}
        records={records}
        columns={columns}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        handleEdit={handleEdit}
        selectable
        selectedRowKeys={selectedIds}
        getRowKey={getRowKey}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        onRowDelete={handleRowDelete}
        pagination={true}
        getHref={(record) => `/posts/${record?.alias || record?._id}`}
      />
    </FilterPageLayout>
  );
};

export default PostsTable;
